/**
 * MobileListCard.js
 *
 * Full-width list-row card that matches native list item style.
 * Used by Notice, Circular, Assignment, Bills, Receipts, etc.
 *
 * Usage:
 *   <MobileListCard
 *     icon={<NotificationsIcon />}
 *     iconColor="#1565C0"
 *     title="Annual Report 2025"
 *     subtitle="Posted: 15 Jan 2025"
 *     rightLabel="Download"
 *     onClick={() => handleDownload(item)}
 *     badge="New"
 *     badgeColor="error"
 *   />
 */
import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Divider,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import colors from '../common/Colors';

const MobileListCard = ({
  icon = null,
  iconColor = colors.BLUE,
  iconBg = null,
  title = '',
  subtitle = '',
  description = '',
  rightLabel = '',
  rightIcon = null,
  onClick = null,
  badge = '',
  badgeColor = 'primary',   // 'primary' | 'secondary' | 'error' | 'warning' | 'success'
  showDivider = true,
  showChevron = true,
  sx = {},
}) => {
  const computedIconBg = iconBg || `${iconColor}1A`; // 10% opacity version of iconColor

  return (
    <>
      <Box
        onClick={onClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          backgroundColor: '#ffffff',
          cursor: onClick ? 'pointer' : 'default',
          minHeight: 64,
          '&:active': onClick ? { backgroundColor: '#f0f4ff' } : {},
          WebkitTapHighlightColor: 'transparent',
          ...sx,
        }}
      >
        {/* Left icon */}
        {icon && (
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '10px',
              backgroundColor: computedIconBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5,
              flexShrink: 0,
              color: iconColor,
              '& svg': { fontSize: 22 },
            }}
          >
            {icon}
          </Box>
        )}

        {/* Text content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                fontSize: '14px',
                color: '#1A1A2E',
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
              }}
            >
              {title}
            </Typography>
            {badge ? (
              <Chip
                label={badge}
                color={badgeColor}
                size="small"
                sx={{ height: 20, fontSize: '10px', flexShrink: 0 }}
              />
            ) : null}
          </Box>

          {subtitle ? (
            <Typography
              variant="body2"
              sx={{
                fontSize: '12px',
                color: '#666666',
                mt: 0.3,
                lineHeight: 1.3,
              }}
            >
              {subtitle}
            </Typography>
          ) : null}

          {description ? (
            <Typography
              variant="body2"
              sx={{
                fontSize: '12px',
                color: '#888888',
                mt: 0.5,
                lineHeight: 1.4,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {description}
            </Typography>
          ) : null}
        </Box>

        {/* Right side */}
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5, flexShrink: 0 }}>
          {rightLabel ? (
            <Typography
              variant="caption"
              sx={{ fontSize: '12px', color: colors.BLUE, fontWeight: 500, mr: 0.5 }}
            >
              {rightLabel}
            </Typography>
          ) : null}
          {rightIcon ? rightIcon : null}
          {showChevron && onClick && !rightIcon ? (
            <ChevronRightIcon sx={{ fontSize: 20, color: '#CCCCCC' }} />
          ) : null}
        </Box>
      </Box>

      {showDivider && (
        <Divider sx={{ ml: icon ? 8 : 2 }} />
      )}
    </>
  );
};

export default MobileListCard;
